/**
 * View contents of a single file from an artifact.
 */

// @todo: Migrate to 'Dialog.Alert' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alert} from '@wandb/weave/components/Alert';
import {
  constString,
  opArtifactVersionFile,
  opFileContents,
  opRootArtifactVersion,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React from 'react';

import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {CodeEditor} from '../CodeEditor';

type ArtifactFileViewerProps = {
  entityName: string;
  projectName: string;
  artifactType: string;
  artifactVersionName: string; // E.g. source-fmnist-codeartifact.py:v1
  path: string;
};

const EXT_TO_LANGUAGE: Record<string, string> = {
  json: 'json',
  py: 'python',
  txt: 'plaintext',
} as const;

export const ArtifactFileViewer = ({
  entityName,
  projectName,
  artifactType,
  artifactVersionName,
  path,
}: ArtifactFileViewerProps) => {
  const artifactVersion = opRootArtifactVersion({
    entityName: constString(entityName),
    projectName: constString(projectName),
    artifactTypeName: constString(artifactType),
    artifactVersionName: constString(artifactVersionName),
  });
  const file = opArtifactVersionFile({
    artifactVersion,
    path: constString(path),
  });
  const contents = opFileContents({file});
  const {result, loading} = useNodeValue(contents);

  if (loading) {
    return <Loader active inline name="artifact-file-viewer-loader" />;
  }
  if (!result) {
    return <Alert severity="error">Error fetching file contents</Alert>;
  }

  const ext = path.split('.').pop() ?? '';
  const language = EXT_TO_LANGUAGE[ext] ?? 'plaintext';

  let formattedResult = result;
  if (ext === 'json') {
    formattedResult = JSON.stringify(JSON.parse(result), null, 4);
  }

  return (
    <CodeEditor value={formattedResult} language={language} readOnly={true} />
  );
};
